import { createEffect, createSignal, type JSX, on } from 'solid-js';
import { t } from '../i18n';
import { Button } from './ui/Button';

// The copyable client request id, as its own control because TWO states now
// render one: `<ErrorState/>` (a fault, which also keeps a Retry) and
// `<DeniedState/>` (an answer, which must not). Quoting an id to support is
// DIAGNOSIS, not recovery — it is the half a 403 keeps — so it could not stay
// welded to the retry surface.
//
// What is shared is the state machine, not the markup: the `copied` latch, its
// reset, and the clipboard failure. The `<code>` that prints the id stays with
// each caller, whose layout differs. A second copy of the latch is what would
// have drifted — the reset below is a rule nobody re-derives on sight.

export type RequestIdCopyProps = {
  readonly requestId: string;
};

export function RequestIdCopy(props: RequestIdCopyProps): JSX.Element {
  const [copied, setCopied] = createSignal(false);

  // A retry can mint a fresh request id, and `<Show>` does not remount on a
  // value change — only on a truthiness change — so the button would otherwise
  // keep claiming "Copied" about an id that was never on the clipboard.
  createEffect(
    on(
      () => props.requestId,
      () => setCopied(false),
    ),
  );

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(props.requestId);
      setCopied(true);
    } catch {
      // ponytail: clipboard blocked (perms / insecure context) — the id is still
      // shown for manual copy, so a silent no-op is fine.
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={copy}>
      {copied() ? t('errors.copied') : t('errors.copy_request_id')}
    </Button>
  );
}
